import React, { useEffect, useState } from 'react';
import { useDeck } from '@/app/context/DeckContext/useDeck';
import RadioFormElement from './radio-form-element/RadioFormElement';
import PageRangeTextbox from './page-range-textbox/PageRangeTextbox';
import DeckAPI from '@/app/api/DeckAPI/DeckAPI';
import { DeckStatus } from '@/app/constants/DeckStatus';
import { FlashcardType, PowerpointContentType } from './models';
import {
  Generator,
  GeneratorOptions
} from '@/app/types/Generator/GeneratorOptions';
import Link from 'next/link';
import { useCustomer } from '@/app/context/CustomerContext/useCustomer';
import { getAllPagesRange, getAllPagesText } from './utils';
import { useRouter } from 'next/router';
import { useFeedback } from '@/app/context/FeedbackContext/useFeedback';
import { ParsedContent } from '@/app/types/Generator/ParsedContent';
import MaxFlashcardsTextbox from './max-flashcards-textbox/MaxFlashcardsTextbox';
import { ContentViewer } from '@/components/common/content-viewer/ContentViewer';
import { useSettings } from '@/app/context/SettingsContext/useSettings';
import Image from 'next/legacy/image';
import { useAnalytics } from '@/app/context/AnalyticsContext/useAnalytics';
import { useModal } from '@/app/context/ModalContext/useModal';
import { ProFeatureKey } from '@/components/common/modal/upgrade/Upgrade';

const GenerateForm = () => {
  const { selectedDeck, generateFlashcards } = useDeck();

  const { generatorBehavior, flashcardLanguage } = useSettings();

  const { trackEvent, analyticsInstance } = useAnalytics();

  const { trackGeneratorActivity } = useFeedback();

  const { openModal } = useModal();

  const { subscription, isProEligible, isPro } = useCustomer();
  const [didTrackUpgrade, setDidTrackUpgrade] = useState(false);
  const [didTrackUpgradePages, setDidTrackUpgradePages] = useState(false);

  const router = useRouter();

  if (!selectedDeck) {
    return null;
  }

  const [flashcardType, setFlashcardType] = React.useState(
    FlashcardType.question
  );
  const [powerpointContentType, setPowerpointContentType] = React.useState(
    PowerpointContentType.slideAndNotes
  );
  const [enablePageSelection, setEnablePageSelection] = React.useState(false);
  const [selectedPages, setSelectedPages] = React.useState<number[]>([]);

  const [showCustomPageRange, setShowCustomPageRange] = React.useState(false);
  const [customPageRangeText, setCustomPageRangeText] = React.useState('');
  const [isCustomPageRangeValid, setIsCustomPageRangeValid] =
    React.useState(false);
  const [customPageRangeErrorMessage, setCustomPageRangeErrorMessage] =
    React.useState('');

  const [showCustomMaxFlashcards, setShowCustomMaxFlashcards] =
    React.useState(false);
  const [maxFlashcards, setMaxFlaschards] = React.useState(-1);
  const [maxFlascardsError, setMaxFlaschardsError] = React.useState('');

  const [minSelectedPage, setMinSelectedPage] = useState(0);

  const [occludeAllText, setOccludeAllText] = useState(false);

  const [parsedContent, setParsedContent] = React.useState<
    ParsedContent[] | null
  >(null);

  const [previewImageUrls, setPreviewImageUrls] = React.useState([]);
  const [isLoadingPreviewImages, setIsLoadingPreviewImages] =
    React.useState(false);

  const [topOffset, setTopOffset] = React.useState(128);

  const handleResize = () => {
    const offset =
      document.getElementById('selected-header').offsetTop +
      document.getElementById('selected-header').clientHeight;

    setTopOffset(offset);
  };

  React.useEffect(() => {
    if (
      !subscription.isPro &&
      isProEligible &&
      enablePageSelection &&
      selectedDeck.pageCount > subscription.pagesOrSlidesPerUpload &&
      !didTrackUpgrade
    ) {
      setDidTrackUpgrade(true);
      trackEvent(analyticsInstance, 'upgrade_visible', {
        upgrade_source: 'generate_form_pages'
      });
    }
  }, [subscription, isProEligible, enablePageSelection]);

  React.useEffect(() => {
    if (
      !subscription.isPro &&
      isProEligible &&
      subscription.uploadsRemaining === 0 &&
      !didTrackUpgradePages
    ) {
      setDidTrackUpgradePages(true);
      trackEvent(analyticsInstance, 'upgrade_visible', {
        upgrade_source: 'generate_form_uploads'
      });
    }
  }, [subscription, isProEligible]);

  React.useEffect(() => {
    window.addEventListener('resize', handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  React.useEffect(() => {
    if (selectedPages.length === 1) {
      const contentViewer = document.getElementById(
        'content_viewer_' + selectedPages[0]
      );

      if (contentViewer) {
        contentViewer.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }

      const newMin = Math.min(...selectedPages);

      if (newMin < minSelectedPage || minSelectedPage === 0) {
        setMinSelectedPage(newMin);
      }
    } else {
      setMinSelectedPage(0);
    }
  }, [selectedPages]);

  React.useEffect(() => {
    if (selectedDeck.status === DeckStatus.Ready) {
      setIsLoadingPreviewImages(true);
      DeckAPI.getPreviewImages(selectedDeck.id).then((urls) => {
        setPreviewImageUrls(urls);
        setIsLoadingPreviewImages(false);
      });
    }
  }, [selectedDeck.status]);

  React.useEffect(() => {
    if (flashcardType === FlashcardType.imageOcclusion) {
      setMaxFlaschards(-1);
    }
  }, [flashcardType]);

  const onParsedContentChange = (parsedContent: ParsedContent[] | null) => {
    setParsedContent(parsedContent);
  };

  const onCustomPageRangeChange = (
    textValue: string,
    pages: number[],
    isValid: boolean,
    errorMessage: string
  ) => {
    setCustomPageRangeText(textValue);
    setIsCustomPageRangeValid(isValid);
    setCustomPageRangeErrorMessage(errorMessage);

    if (isValid) {
      setSelectedPages(pages);
    } else {
      setSelectedPages([]);
    }
  };

  const getMaxFlashcardsSelectedOption = () => {
    if (showCustomMaxFlashcards) {
      return 4;
    } else if (maxFlashcards === -1) {
      return 0;
    } else if (maxFlashcards === 100) {
      return 1;
    } else if (maxFlashcards === 50) {
      return 2;
    } else if (maxFlashcards === 25) {
      return 3;
    } else {
      return 0;
    }
  };

  const onMaxFlaschardsChange = (index: number) => {
    if (index === 0) {
      setMaxFlaschards(-1);
      setMaxFlaschardsError('');
      setShowCustomMaxFlashcards(false);
    } else if (index === 1) {
      setMaxFlaschards(100);
      setMaxFlaschardsError('');
      setShowCustomMaxFlashcards(false);
    } else if (index === 2) {
      setMaxFlaschards(50);
      setMaxFlaschardsError('');
      setShowCustomMaxFlashcards(false);
    } else if (index === 3) {
      setMaxFlaschards(25);
      setMaxFlaschardsError('');
      setShowCustomMaxFlashcards(false);
    } else {
      setMaxFlaschards(0);
      setMaxFlaschardsError('');
      setShowCustomMaxFlashcards(true);
    }
  };

  const onCustomMaxFlaschardsChange = (
    value: number,
    isValid: boolean,
    errorMessage: string
  ) => {
    setMaxFlaschards(value);
    setMaxFlaschardsError(errorMessage);
  };

  useEffect(() => {
    setEnablePageSelection(
      selectedDeck &&
        (selectedDeck.fileType === 'pptx' ||
          selectedDeck.fileType === 'pdf' ||
          selectedDeck.fileType === 'docx' ||
          (selectedDeck.fileType === 'txt' && !selectedDeck?.videoId))
    );
  }, [selectedDeck]);

  useEffect(() => {
    if (enablePageSelection && selectedDeck) {
      if (showCustomPageRange) {
        setSelectedPages([]);
      } else {
        setSelectedPages(
          getAllPagesRange(
            selectedDeck.pageCount,
            subscription.pagesOrSlidesPerUpload
          )
        );
      }
    }
  }, [enablePageSelection, selectedDeck, showCustomPageRange, subscription]);

  const isPowerpoint = selectedDeck.fileType === 'pptx';

  let generator: keyof typeof Generator = 'Question';
  if (flashcardType === FlashcardType.cloze) {
    generator = 'Cloze';
  } else if (flashcardType === FlashcardType.imageOcclusion) {
    generator = 'ImageOcclusion';
  }

  const onSubmit = () => {
    const options: GeneratorOptions = {
      generator,
      customPages: selectedPages.length > 0 ? selectedPages : [1],
      skipParsing: false,
      generatorBehavior,
      flashcardLanguage,
      maxFlashcards,
      occludeAllText
    };

    if (isPowerpoint) {
      (options.includeSlideContent = isPowerpoint
        ? powerpointContentType === PowerpointContentType.slideAndNotes ||
          powerpointContentType === PowerpointContentType.slideOnly
        : undefined),
        (options.includeNotesSection = isPowerpoint
          ? powerpointContentType === PowerpointContentType.slideAndNotes ||
            powerpointContentType === PowerpointContentType.notesOnly
          : undefined);
    }

    trackEvent(analyticsInstance, 'generate_form_click_upload', {
      deck_id: selectedDeck.id,
      file_type: selectedDeck.fileType,
      ...options
    });

    generateFlashcards(
      selectedDeck.id,
      options,
      (selectedDeck.fileType === 'pdf' ||
        selectedDeck.fileType === 'docx' ||
        selectedDeck.fileType === 'txt') &&
        !selectedDeck?.videoId
        ? parsedContent
        : null
    );

    trackGeneratorActivity({
      deckId: selectedDeck.id,
      fileType: selectedDeck.fileType,
      flashcardType:
        flashcardType === FlashcardType.question ? 'Question' : 'Cloze',
      isAllPages: !showCustomPageRange,
      selectedPages: selectedPages.length,
      totalPages: selectedDeck.pageCount,
      includeSlideContent: options.includeSlideContent ?? false,
      includeNotesSection: options.includeNotesSection ?? false,
      isPro,
      isProEligible
    });
  };

  let isValid = !showCustomPageRange || isCustomPageRangeValid;

  let generateButtonText = `Generate Flaschards ${
    enablePageSelection
      ? `(${selectedPages.length} ${isPowerpoint ? 'Slides' : 'Pages'})`
      : ''
  }  
  `;

  if (subscription.uploadsRemaining === 0) {
    generateButtonText = '0 uploads remaining';
    isValid = false;
  }

  if (maxFlashcards === 0) {
    isValid = false;
  }

  const textFlashcardTypeOptions = [
    FlashcardType.question,
    FlashcardType.cloze
  ];

  const allFlashcardTypeOptions = [
    FlashcardType.question,
    FlashcardType.cloze,
    FlashcardType.imageOcclusion
  ];

  const powerpointContentTypeOptions = [
    PowerpointContentType.slideAndNotes,
    PowerpointContentType.slideOnly,
    PowerpointContentType.notesOnly
  ];

  const textOcclusionOptions = [
    'Labels and Annotations (Recommended)',
    'All Text'
  ];

  return (
    <div
      className="fixed flex w-full"
      style={{
        height: `calc(100% - ${topOffset}px)`
      }}
    >
      <ContentViewer
        selectedPages={enablePageSelection ? selectedPages : undefined}
        onParsedContentChange={onParsedContentChange}
        hideCustomOptions={flashcardType === FlashcardType.imageOcclusion}
      />

      <div className="flex h-full flex-1 flex-col bg-base-100 md:min-w-[500px]">
        <div className="flex-1 overflow-y-auto">
          <div className="pl-6 pr-6 pt-2">
            <RadioFormElement
              title="What type of flashcards do you want?"
              options={
                selectedDeck.fileType === 'txt'
                  ? textFlashcardTypeOptions
                  : allFlashcardTypeOptions
              }
              onSelect={(index) =>
                setFlashcardType(allFlashcardTypeOptions[index])
              }
              selectedOption={allFlashcardTypeOptions.indexOf(flashcardType)}
              popularOption={FlashcardType.question}
              showLanguage
            />
            {flashcardType !== FlashcardType.imageOcclusion && (
              <RadioFormElement
                title={`How many flashcards do you want?`}
                helpText={
                  'Limbiks will prioritize an even distribution of the most important flashcards for the amount chosen.  Limbiks may generate less flaschards than the chosen amount.'
                }
                options={[
                  'All available',
                  '100 or less',
                  '50 or less',
                  '25 or less',
                  'Custom'
                ]}
                onSelect={onMaxFlaschardsChange}
                selectedOption={getMaxFlashcardsSelectedOption()}
                showGeneratorBehavior
              />
            )}
            {showCustomMaxFlashcards && (
              <>
                <MaxFlashcardsTextbox
                  textValue={
                    maxFlashcards === 0 ? '' : maxFlashcards.toString()
                  }
                  onChange={onCustomMaxFlaschardsChange}
                />
                <div className="float-right text-sm text-red">
                  {maxFlascardsError}
                </div>
              </>
            )}
            {isPowerpoint && flashcardType !== FlashcardType.imageOcclusion && (
              <RadioFormElement
                title="What content do you want to use?"
                // helpUrl="TBD"
                options={powerpointContentTypeOptions}
                onSelect={(index) =>
                  setPowerpointContentType(powerpointContentTypeOptions[index])
                }
                selectedOption={powerpointContentTypeOptions.indexOf(
                  powerpointContentType
                )}
              />
            )}
            {flashcardType === FlashcardType.imageOcclusion && (
              <RadioFormElement
                title="What text do you want to occlude?"
                options={textOcclusionOptions}
                onSelect={(index) => setOccludeAllText(index === 1)}
                selectedOption={occludeAllText ? 1 : 0}
              />
            )}
            {enablePageSelection && (
              <RadioFormElement
                title={`Which ${
                  isPowerpoint ? 'slides' : 'pages'
                } do you want to use?`}
                options={[
                  getAllPagesText(
                    selectedDeck.pageCount,
                    subscription.pagesOrSlidesPerUpload,
                    isPowerpoint
                  ),
                  'Custom'
                ]}
                onSelect={(index) => setShowCustomPageRange(index === 1)}
                selectedOption={showCustomPageRange ? 1 : 0}
              />
            )}
            {showCustomPageRange && (
              <>
                <PageRangeTextbox
                  textValue={customPageRangeText}
                  onChange={onCustomPageRangeChange}
                  pageCount={selectedDeck.pageCount}
                  maxPagesAllowed={subscription.pagesOrSlidesPerUpload}
                  isPowerpoint={isPowerpoint}
                />
                <div className="float-right text-sm text-red">
                  {customPageRangeErrorMessage}
                </div>
              </>
            )}
            {!subscription.isPro &&
              isProEligible &&
              enablePageSelection &&
              selectedDeck.pageCount > subscription.pagesOrSlidesPerUpload && (
                <div className="bold te mb-4 ml-0 mr-0 mt-8 border border-white p-4 text-center md:ml-16 md:mr-16">
                  <div>
                    <span>
                      <span
                        onClick={() => {
                          trackEvent(analyticsInstance, 'upgrade_click', {
                            upgrade_source: 'generate_form_pages'
                          });
                          trackEvent(
                            analyticsInstance,
                            'generate_form_click_upgrade_pages',
                            {
                              deck_id: selectedDeck.id,
                              file_type: selectedDeck.fileType
                            }
                          );
                          openModal(
                            'upgrade',
                            'pagesAndSlides' as ProFeatureKey
                          );
                        }}
                        className="link"
                        style={{ color: 'tomato' }}
                      >
                        Upgrade to Limbiks Pro
                      </span>
                    </span>{' '}
                    to select up to 200 {isPowerpoint ? 'slides' : 'pages'} per
                    upload.
                  </div>
                </div>
              )}
            {!subscription.isPro &&
              isProEligible &&
              subscription.uploadsRemaining === 0 && (
                <div className="bold te mb-4 ml-0 mr-0 mt-8 border border-white p-4 text-center md:ml-16 md:mr-16">
                  <div>
                    <span>
                      <span
                        onClick={() => {
                          trackEvent(analyticsInstance, 'upgrade_click', {
                            upgrade_source: 'generate_form_uploads'
                          });
                          trackEvent(
                            analyticsInstance,
                            'generate_form_click_upgrade_uploads',
                            {
                              deck_id: selectedDeck.id,
                              file_type: selectedDeck.fileType
                            }
                          );

                          openModal('upgrade', 'uploads' as ProFeatureKey);
                        }}
                        className="link"
                        style={{ color: 'tomato' }}
                      >
                        Upgrade to Limbiks Pro
                      </span>
                    </span>{' '}
                    for 100 uploads per month.
                  </div>
                </div>
              )}
          </div>
        </div>

        {!parsedContent && isValid && selectedDeck.fileType === 'pdf' ? (
          <>
            <div className="mb-4 ml-4 mr-4 mt-4 md:ml-16 md:mr-16">
              <div
                className={`float-right inline-flex w-full transform items-center justify-center rounded bg-primary px-4 py-2
              font-bold text-base-content opacity-50 transition duration-300 ease-in-out`}
              >
                <Image
                  alt="logo"
                  height={24}
                  width={24}
                  src="/white-logo.png"
                />
                <span className="pl-3">Loading File...</span>
              </div>
            </div>
          </>
        ) : (
          <div className="mb-4 ml-4 mr-4 mt-4 md:ml-16 md:mr-16">
            <div
              onClick={isValid ? onSubmit : undefined}
              className={`${
                !isValid
                  ? 'opacity-50'
                  : 'cursor-pointer hover:scale-105 hover:underline'
              } float-right inline-flex w-full transform items-center justify-center rounded bg-primary px-4
              py-2 font-bold text-primary-content transition duration-300 ease-in-out`}
            >
              <img width={24} src="/white-logo.png" />
              <span className="pl-3">{generateButtonText}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerateForm;
